import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { useAuth } from "./auth";

const BillingContext = createContext({
  billing: null,
  loading: true,
  refresh: () => {},
  upgradeModal: { open: false, reason: null },
  openUpgradeModal: () => {},
  closeUpgradeModal: () => {},
});

const TIER_RANK = { free: 0, pro: 1, business: 2 };

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, reason: null });

  const refresh = useCallback(async () => {
    if (!user) {
      setBilling(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/billing/me");
      setBilling(data);
    } catch (_) {
      // soft-fail; keep last known billing
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const openUpgradeModal = useCallback((reason) => {
    setUpgradeModal({ open: true, reason: reason || null });
  }, []);
  const closeUpgradeModal = useCallback(() => {
    setUpgradeModal({ open: false, reason: null });
  }, []);

  return (
    <BillingContext.Provider value={{ billing, loading, refresh, upgradeModal, openUpgradeModal, closeUpgradeModal }}>
      {children}
    </BillingContext.Provider>
  );
}

export const useBilling = () => useContext(BillingContext);

/**
 * Helper: extract a tier-limit error from an axios catch block.
 * Returns { code, message, tier, limit_key, current, maximum } or null.
 */
export function parseTierLimitError(err) {
  const detail = err?.response?.data?.detail;
  if (err?.response?.status === 402 && detail && typeof detail === "object" && detail.code === "tier_limit") {
    return detail;
  }
  return null;
}

export { TIER_RANK };
